/**
 * Validazione Codice Fiscale Italiano
 * Verifica formato e carattere di controllo (checksum)
 */

// Tabella caratteri pari (posizioni 2,4,6,8,10,12,14)
const EVEN_CHAR_VALUES = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18,
  'T': 19, 'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
};

// Tabella caratteri dispari (posizioni 1,3,5,7,9,11,13,15)
const ODD_CHAR_VALUES = {
  '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
  'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
};

// Tabella per calcolare il carattere di controllo dal resto
const CONTROL_CHAR = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Verifica se il formato del codice fiscale è corretto
 * Pattern: AAABBB00A00A000A
 * - 6 lettere (cognome + nome)
 * - 2 cifre (anno)
 * - 1 lettera (mese)
 * - 2 cifre (giorno)
 * - 1 lettera + 3 cifre (comune)
 * - 1 lettera (controllo)
 */
export const isValidCFFormat = (cf) => {
  if (!cf || typeof cf !== 'string') return false;
  
  const upperCF = cf.toUpperCase().trim();
  
  // Deve essere lungo 16 caratteri
  if (upperCF.length !== 16) return false;
  
  // Pattern regex per il formato
  const cfRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
  return cfRegex.test(upperCF);
};

/**
 * Calcola il carattere di controllo (checksum) del codice fiscale
 */
export const calculateControlChar = (cf) => {
  if (!cf || cf.length < 15) return null;
  
  const upperCF = cf.toUpperCase().trim();
  let sum = 0;
  
  // Somma i valori dei primi 15 caratteri
  for (let i = 0; i < 15; i++) {
    const char = upperCF[i];
    // Posizioni dispari (1,3,5...) -> indice pari (0,2,4...)
    // Posizioni pari (2,4,6...) -> indice dispari (1,3,5...)
    if (i % 2 === 0) {
      // Posizione dispari nel CF
      sum += ODD_CHAR_VALUES[char] || 0;
    } else {
      // Posizione pari nel CF
      sum += EVEN_CHAR_VALUES[char] || 0;
    }
  }
  
  // Il carattere di controllo è il resto della divisione per 26
  return CONTROL_CHAR[sum % 26];
};

/**
 * Verifica se il codice fiscale è valido (formato + checksum)
 */
export const isValidCodiceFiscale = (cf) => {
  if (!isValidCFFormat(cf)) return false;
  
  const upperCF = cf.toUpperCase().trim();
  const expectedControlChar = calculateControlChar(upperCF);
  const actualControlChar = upperCF[15];
  
  return expectedControlChar === actualControlChar;
};

/**
 * Restituisce un messaggio di errore dettagliato
 */
export const getCodiceFiscaleError = (cf) => {
  if (!cf || cf.trim() === '') {
    return 'Il codice fiscale è obbligatorio';
  }
  
  const upperCF = cf.toUpperCase().trim();
  
  if (upperCF.length !== 16) {
    return `Il codice fiscale deve essere di 16 caratteri (attuale: ${upperCF.length})`;
  }
  
  if (!isValidCFFormat(upperCF)) {
    return 'Formato codice fiscale non valido (es. RSSMRA85M01H501Z)';
  }
  
  const expectedControlChar = calculateControlChar(upperCF);
  const actualControlChar = upperCF[15];
  
  if (expectedControlChar !== actualControlChar) {
    return `Carattere di controllo non valido (atteso: ${expectedControlChar})`;
  }
  
  return null; // Nessun errore
};

/**
 * Formatta il codice fiscale (uppercase e rimuove spazi)
 */
export const formatCodiceFiscale = (cf) => {
  if (!cf) return '';
  return cf.toUpperCase().replace(/\s/g, '');
};

export default {
  isValidCodiceFiscale,
  isValidCFFormat,
  calculateControlChar,
  getCodiceFiscaleError,
  formatCodiceFiscale
};
